import {isEmpty, map} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Card, Chip, Icon} from 'components';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.REQUEST_APPOINTMENT_SECTION.RECIPIENT_EMAILS_MODAL;
const MAX_COUNT = 4;

type Props = {
    title: string;
    group?: Group;
    onEdit: (group: Group) => void;
    onReset: (group: Group) => void;
    emails: string[];
};

export const RecipientCard: React.FC<Props> = ({title, group, emails, onEdit, onReset}: Props) => {
    const [isAllShowed, setIsAllShowed] = useState<boolean>(false);
    const {t} = useTranslation();

    const handleEdit = useCallback(() => {
        onEdit(group);
    }, [group, onEdit]);

    const handleClose = useCallback(() => {
        onReset(group);
    }, [group, onReset]);

    const canExpand = useMemo(() => emails?.length > MAX_COUNT, [emails]);

    if (isEmpty(emails)) {
        return null;
    }

    return (
        <Card className='tw-my-4 tw-p-4'>
            <div className='tw-flex tw-justify-between'>
                <div className='tw-ts-h5-bold tw-ml-7 tw-mb-2'>{title}</div>
                <div className='tw-flex tw-ml-auto'>
                    <Icon
                        name='edit'
                        color='primary'
                        button
                        group
                        onClick={handleEdit}
                    />
                    <Icon
                        name='delete'
                        color='alert'
                        button
                        onClick={handleClose}
                    />
                </div>
            </div>
            <div className='tw-flex tw-items-baseline'>
                <Icon name='paper-clip' color='default-dimmed' className='-md:tw-hidden tw-flex-shrink-0 tw-pt-1.5' />
                <div className='tw-flex tw-flex-wrap tw-items-center'>
                    {map(emails, (email: string, index) => {
                        if (canExpand && !isAllShowed && MAX_COUNT <= index) {
                            return null;
                        }
                        return (
                            <div key={email} className='tw-flex tw-items-center -md:tw-w-full'>
                                <Icon name='user' color='default-dimmed' className='tw-block md:tw-hidden tw-flex-shrink-0' />
                                <Chip>{email}</Chip>
                            </div>
                        );
                    })}
                    {canExpand && !isAllShowed && (
                        <Chip onClick={() => setIsAllShowed(true)}>
                            {'+'}
                            {emails.length - MAX_COUNT}
                            <span className='-md:tw-text-primary'>{` ${t(T_KEYS.BUTTONS.MORE)}`}</span>
                        </Chip>
                    )}
                    {canExpand && isAllShowed && (
                        <Chip onClick={() => setIsAllShowed(false)}>
                            <span className='-md:tw-text-primary'>{t(T_KEYS.BUTTONS.LESS)}</span>
                        </Chip>
                    )}
                </div>
            </div>
        </Card>
    );
};